// PricingTable: sortable, filterable table of plans with provider + category + region filters.

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { t, categoryLabel } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import type { Plan, Provider, Diff } from '../lib/api';
import { formatUSD, formatGB, formatDays } from '../lib/format';
import { VerifiedBadge } from './VerifiedBadge';

interface PricingTableProps {
  plans: Plan[];
  providers: Provider[];
  diffs?: Diff[];
  lang: Lang;
  code: string;
}

type SortKey = 'provider' | 'region' | 'data' | 'validity' | 'price' | 'perGB' | 'promoPrice';
type SortDir = 'asc' | 'desc';

const REGION_FILTERS = ['all', 'global', 'europe', 'us', 'asia', 'turkey'] as const;
type RegionFilter = (typeof REGION_FILTERS)[number];

const CATEGORY_FILTERS = ['all', 'consumer_esim', 'corporate_iot', 'airline_wifi'] as const;
type CategoryFilter = (typeof CATEGORY_FILTERS)[number];

function getPricePerGB(plan: Plan): number | null {
  if (!plan.price_usd || !plan.data_gb || plan.data_gb <= 0) return null;
  return plan.price_usd / plan.data_gb;
}

export function PricingTable({ plans, providers, diffs, lang, code }: PricingTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('perGB');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [regionFilter, setRegionFilter] = useState<RegionFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [unlimitedOnly, setUnlimitedOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [maxPerGB, setMaxPerGB] = useState<number | null>(null);

  const providerMap = useMemo(
    () => Object.fromEntries(providers.map((p) => [p.id, p])),
    [providers]
  );

  // Product-team ask: "Sadece değişimleri mi görüyoruz? Eski/yeni görebilir miyiz?"
  // Build a lookup of prior-week $/GB indexed by provider+region (case-insensitive),
  // so we can show prev → current alongside any plan whose provider×region moved.
  const prevPerGBByProviderRegion = useMemo(() => {
    const m = new Map<string, { prev: number; deltaPct: number | null }>();
    if (!diffs) return m;
    for (const d of diffs) {
      if (d.entity_type !== 'provider') continue;
      if (!d.field.startsWith('price_per_gb::')) continue;
      const region = d.field.substring('price_per_gb::'.length).toLowerCase();
      const prev = d.prev_value != null ? parseFloat(d.prev_value) : NaN;
      if (isNaN(prev)) continue;
      m.set(`${d.entity_id.toLowerCase()}::${region}`, { prev, deltaPct: d.delta_pct });
    }
    return m;
  }, [diffs]);

  function getPrevPerGB(plan: Plan): { prev: number; deltaPct: number | null } | null {
    if (!plan.region) return null;
    return (
      prevPerGBByProviderRegion.get(`${plan.provider_id.toLowerCase()}::${plan.region.toLowerCase()}`) ?? null
    );
  }

  // Providers filtered by category for the provider dropdown
  const providersInCategory = useMemo(() => {
    if (categoryFilter === 'all') return providers;
    return providers.filter((p) => p.category === categoryFilter);
  }, [providers, categoryFilter]);

  const filtered = useMemo(() => {
    return plans.filter((plan) => {
      const provider = providerMap[plan.provider_id];

      // Category filter
      if (categoryFilter !== 'all' && provider?.category !== categoryFilter) return false;

      // Provider filter
      if (providerFilter !== 'all' && plan.provider_id !== providerFilter) return false;

      // Region filter
      if (regionFilter !== 'all') {
        const r = (plan.region ?? '').toLowerCase();
        if (!r.includes(regionFilter)) return false;
      }

      // Toggles
      if (unlimitedOnly && plan.data_unlimited !== 1) return false;
      if (verifiedOnly && plan.is_verified !== 1) return false;

      // Max $/GB
      if (maxPerGB != null) {
        const perGB = getPricePerGB(plan);
        if (perGB == null || perGB > maxPerGB) return false;
      }

      return true;
    });
  }, [plans, providerMap, categoryFilter, providerFilter, regionFilter, unlimitedOnly, verifiedOnly, maxPerGB]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let aVal: string | number | null = null;
      let bVal: string | number | null = null;

      switch (sortKey) {
        case 'provider':
          aVal = providerMap[a.provider_id]?.name ?? a.provider_id;
          bVal = providerMap[b.provider_id]?.name ?? b.provider_id;
          break;
        case 'region': aVal = a.region ?? ''; bVal = b.region ?? ''; break;
        case 'data': aVal = a.data_gb ?? -1; bVal = b.data_gb ?? -1; break;
        case 'validity': aVal = a.validity_days ?? -1; bVal = b.validity_days ?? -1; break;
        case 'price': aVal = a.price_usd ?? Infinity; bVal = b.price_usd ?? Infinity; break;
        case 'perGB': aVal = getPricePerGB(a) ?? Infinity; bVal = getPricePerGB(b) ?? Infinity; break;
        case 'promoPrice': aVal = a.promo_price_usd ?? a.price_usd ?? Infinity; bVal = b.promo_price_usd ?? b.price_usd ?? Infinity; break;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      const aNum = aVal as number;
      const bNum = bVal as number;
      return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
    });
  }, [filtered, sortKey, sortDir, providerMap]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function SortButton({ colKey, label }: { colKey: SortKey; label: string }) {
    return (
      <button onClick={() => toggleSort(colKey)} className="flex items-center gap-1">
        {label}
        {sortKey === colKey && (
          <span className="text-emerald-600">{sortDir === 'asc' ? '↑' : '↓'}</span>
        )}
      </button>
    );
  }

  function resetFilters() {
    setRegionFilter('all');
    setCategoryFilter('all');
    setProviderFilter('all');
    setUnlimitedOnly(false);
    setVerifiedOnly(false);
    setMaxPerGB(null);
  }

  const hasActiveFilters =
    regionFilter !== 'all' || categoryFilter !== 'all' || providerFilter !== 'all' ||
    unlimitedOnly || verifiedOnly || maxPerGB != null;

  return (
    <div className="space-y-3">
      {/* Filter bar — Category */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-neutral-500 uppercase tracking-wider w-20 shrink-0">
            {lang === 'tr' ? 'Kategori' : 'Category'}
          </span>
          {CATEGORY_FILTERS.map((c) => (
            <button
              key={c}
              onClick={() => {
                setCategoryFilter(c);
                // Reset provider filter when category changes
                if (providerFilter !== 'all') setProviderFilter('all');
              }}
              className={`filter-chip ${categoryFilter === c ? 'active' : ''}`}
            >
              {c === 'all' ? (lang === 'tr' ? 'Tümü' : 'All') : categoryLabel(c, lang)}
            </button>
          ))}
        </div>

        {/* Filter bar — Region */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-neutral-500 uppercase tracking-wider w-20 shrink-0">
            {lang === 'tr' ? 'Bölge' : 'Region'}
          </span>
          {REGION_FILTERS.map((r) => (
            <button
              key={r}
              onClick={() => setRegionFilter(r)}
              className={`filter-chip ${regionFilter === r ? 'active' : ''}`}
            >
              {r === 'all' ? t('allRegions', lang) : t(r, lang)}
            </button>
          ))}
        </div>

        {/* Filter bar — Provider dropdown + toggles + max $/GB */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-neutral-500 uppercase tracking-wider w-20 shrink-0">
            {lang === 'tr' ? 'Sağlayıcı' : 'Provider'}
          </span>
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="bg-neutral-100 border border-neutral-300 rounded text-xs text-neutral-800 px-2 py-1
                       hover:border-neutral-500 focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="all">{lang === 'tr' ? 'Tüm sağlayıcılar' : 'All providers'}</option>
            {providersInCategory.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <div className="w-px bg-neutral-200 mx-1 h-5" />

          <button
            onClick={() => setUnlimitedOnly(!unlimitedOnly)}
            className={`filter-chip ${unlimitedOnly ? 'active' : ''}`}
          >
            {t('unlimitedOnly', lang)}
          </button>
          <button
            onClick={() => setVerifiedOnly(!verifiedOnly)}
            className={`filter-chip ${verifiedOnly ? 'active' : ''}`}
          >
            {t('verifiedOnly', lang)}
          </button>

          <div className="w-px bg-neutral-200 mx-1 h-5" />

          {/* Max $/GB filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-neutral-500 uppercase tracking-wider">
              {lang === 'tr' ? 'Max $/GB' : 'Max $/GB'}
            </span>
            <input
              type="number"
              min="0"
              step="0.5"
              placeholder="—"
              value={maxPerGB ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setMaxPerGB(v === '' ? null : Number(v));
              }}
              className="bg-neutral-100 border border-neutral-300 rounded text-xs text-neutral-800 px-2 py-1 w-16
                         hover:border-neutral-500 focus:border-emerald-500/50 focus:outline-none tabular-nums"
            />
          </div>

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="text-xs text-neutral-500 hover:text-neutral-700 transition-colors px-2 py-1"
            >
              {lang === 'tr' ? '× Filtreleri temizle' : '× Clear filters'}
            </button>
          )}
        </div>
      </div>

      {/* Row count */}
      <div className="text-xs text-neutral-500">
        {sorted.length} / {plans.length} {lang === 'tr' ? 'plan' : 'plans'}
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="data-table min-w-[900px]">
          <thead>
            <tr>
              <th><SortButton colKey="provider" label={t('provider', lang)} /></th>
              <th><SortButton colKey="region" label={t('region', lang)} /></th>
              <th>{t('plan', lang)}</th>
              <th><SortButton colKey="data" label={t('data', lang)} /></th>
              <th><SortButton colKey="validity" label={t('validity', lang)} /></th>
              <th><SortButton colKey="price" label={t('priceUSD', lang)} /></th>
              <th>
                <div className="flex flex-col leading-tight">
                  <span>{lang === 'tr' ? 'Önceki $/GB' : 'Prev $/GB'}</span>
                  <span className="text-[10px] text-neutral-500 font-normal">{lang === 'tr' ? '(provider×bölge)' : '(provider×region)'}</span>
                </div>
              </th>
              <th><SortButton colKey="perGB" label={t('perGB', lang)} /></th>
              <th><SortButton colKey="promoPrice" label={t('promo', lang)} /></th>
              <th>{t('verified', lang)}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-center text-neutral-500 py-8">{t('noData', lang)}</td>
              </tr>
            ) : (
              sorted.map((plan) => {
                const provider = providerMap[plan.provider_id];
                const perGB = getPricePerGB(plan);
                const prevInfo = getPrevPerGB(plan);
                return (
                  <tr key={plan.id}>
                    <td>
                      <Link
                        to={`/providers/${plan.provider_id}?code=${code}`}
                        className="text-neutral-800 hover:text-emerald-600 transition-colors font-medium text-xs"
                      >
                        {provider?.name ?? plan.provider_id}
                      </Link>
                    </td>
                    <td className="text-neutral-600">{plan.region ?? '-'}</td>
                    <td className="text-neutral-600 max-w-[160px] truncate text-xs">{plan.plan_name ?? '-'}</td>
                    <td className="tabular-nums text-neutral-700">
                      {plan.data_unlimited === 1
                        ? <span className="text-sky-600 text-xs">{t('unlimited', lang)}</span>
                        : formatGB(plan.data_gb)}
                    </td>
                    <td className="tabular-nums text-neutral-600">{formatDays(plan.validity_days)}</td>
                    <td className="tabular-nums text-neutral-800">{formatUSD(plan.price_usd)}</td>
                    <td className="tabular-nums text-xs">
                      {prevInfo ? (
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-neutral-500">{formatUSD(prevInfo.prev)}</span>
                          {prevInfo.deltaPct != null && (
                            <span
                              className={
                                prevInfo.deltaPct > 0
                                  ? 'text-rose-600/80 text-[10px]'
                                  : prevInfo.deltaPct < 0
                                    ? 'text-emerald-600/80 text-[10px]'
                                    : 'text-neutral-500 text-[10px]'
                              }
                            >
                              {prevInfo.deltaPct > 0 ? '+' : ''}{prevInfo.deltaPct.toFixed(0)}%
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-neutral-300">—</span>
                      )}
                    </td>
                    <td className="tabular-nums font-medium text-emerald-600">{perGB != null ? formatUSD(perGB) : '-'}</td>
                    <td>
                      {plan.promo_price_usd != null ? (
                        <div>
                          <span className="tabular-nums text-amber-600 text-xs">{formatUSD(plan.promo_price_usd)}</span>
                          {plan.promo_label && (
                            <span className="text-neutral-500 text-xs ml-1">({plan.promo_label})</span>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                    <td>
                      <VerifiedBadge verified={plan.is_verified === 1} lang={lang} />
                      {plan.source_url && plan.source_url.startsWith('http') && (
                        plan.source_reachable === 0 ? (
                          <span
                            title={t('sourceUnreachableHint', lang)}
                            className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-[1px] ml-1.5"
                          >
                            {t('sourceUnreachable', lang)}
                          </span>
                        ) : (
                          <a
                            href={plan.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] text-neutral-500 hover:text-emerald-600 ml-1.5 underline decoration-dotted"
                          >
                            {lang === 'tr' ? 'Kaynak' : 'Source'}
                          </a>
                        )
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
